import { useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BadgeAlert, CircleCheckBig, LifeBuoy, Search, Ticket, TimerReset } from 'lucide-react';

type TicketStatus = 'open' | 'in_progress' | 'resolved' | 'escalated';
type TicketPriority = 'low' | 'medium' | 'high' | 'urgent';

interface SupportTicket {
  id: string;
  title: string;
  school: string;
  owner: string;
  createdAt: string;
  status: TicketStatus;
  priority: TicketPriority;
}

const INITIAL_TICKETS: SupportTicket[] = [
  { id: 'TKT-1001', title: 'Unable to assign school admin role', school: 'North Valley High', owner: 'Mia Thompson', createdAt: '2026-04-06T09:20:00Z', status: 'open', priority: 'high' },
  { id: 'TKT-1002', title: 'Incident feed latency in dashboard', school: 'Lakeside Academy', owner: 'Ethan Brooks', createdAt: '2026-04-05T16:14:00Z', status: 'in_progress', priority: 'medium' },
  { id: 'TKT-1003', title: 'Bulk import failed for student list', school: 'Westfield Prep', owner: 'Nina Patel', createdAt: '2026-04-05T11:43:00Z', status: 'escalated', priority: 'urgent' },
  { id: 'TKT-1004', title: 'Need audit export for district review', school: 'East Brook School', owner: 'Logan Rivera', createdAt: '2026-04-04T13:08:00Z', status: 'resolved', priority: 'low' },
];

const STATUS_STYLES: Record<TicketStatus, string> = {
  open: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-amber-100 text-amber-700',
  resolved: 'bg-emerald-100 text-emerald-700',
  escalated: 'bg-red-100 text-red-700',
};

const PRIORITY_STYLES: Record<TicketPriority, string> = {
  low: 'bg-slate-100 text-slate-700',
  medium: 'bg-blue-100 text-blue-700',
  high: 'bg-orange-100 text-orange-700',
  urgent: 'bg-red-100 text-red-700',
};

export function SystemAdminTickets() {
  const [tickets, setTickets] = useState<SupportTicket[]>(INITIAL_TICKETS);
  const [statusFilter, setStatusFilter] = useState<'all' | TicketStatus>('all');
  const [priorityFilter, setPriorityFilter] = useState<'all' | TicketPriority>('all');
  const [query, setQuery] = useState('');

  const filteredTickets = useMemo(
    () =>
      tickets.filter((ticket) => {
        const matchesStatus = statusFilter === 'all' || ticket.status === statusFilter;
        const matchesPriority = priorityFilter === 'all' || ticket.priority === priorityFilter;
        const text = `${ticket.id} ${ticket.title} ${ticket.school} ${ticket.owner}`.toLowerCase();
        const matchesQuery = query.trim().length === 0 || text.includes(query.toLowerCase());
        return matchesStatus && matchesPriority && matchesQuery;
      }),
    [tickets, statusFilter, priorityFilter, query],
  );

  const updateStatus = (id: string, status: TicketStatus) => {
    setTickets((prev) => prev.map((ticket) => (ticket.id === id ? { ...ticket, status } : ticket)));
  };

  const openCount = tickets.filter((ticket) => ticket.status === 'open').length;
  const escalatedCount = tickets.filter((ticket) => ticket.status === 'escalated').length;
  const resolvedCount = tickets.filter((ticket) => ticket.status === 'resolved').length;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6 flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Open Tickets</p>
              <p className="text-2xl font-bold text-slate-900">{openCount}</p>
            </div>
            <Ticket className="h-7 w-7 text-blue-600" />
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6 flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Escalated</p>
              <p className="text-2xl font-bold text-slate-900">{escalatedCount}</p>
            </div>
            <BadgeAlert className="h-7 w-7 text-red-600" />
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6 flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Resolved (7d)</p>
              <p className="text-2xl font-bold text-slate-900">{resolvedCount}</p>
            </div>
            <CircleCheckBig className="h-7 w-7 text-emerald-600" />
          </CardContent>
        </Card>
      </div>

      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <LifeBuoy className="h-5 w-5" />
            Support Ticketing
          </CardTitle>
          <CardDescription>Track and update platform support requests</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="md:col-span-2 relative">
              <Search className="h-4 w-4 absolute left-3 top-3 text-slate-400" />
              <Input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                className="pl-9"
                placeholder="Search by ticket, school, owner, or issue..."
              />
            </div>
            <Select value={statusFilter} onValueChange={(value) => setStatusFilter(value as 'all' | TicketStatus)}>
              <SelectTrigger>
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="in_progress">In progress</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
                <SelectItem value="escalated">Escalated</SelectItem>
              </SelectContent>
            </Select>
            <Select value={priorityFilter} onValueChange={(value) => setPriorityFilter(value as 'all' | TicketPriority)}>
              <SelectTrigger>
                <SelectValue placeholder="Priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All priorities</SelectItem>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="urgent">Urgent</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-3">
            {filteredTickets.map((ticket) => (
              <div key={ticket.id} className="border border-slate-200 rounded-lg p-4">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-semibold px-2 py-0.5 rounded bg-slate-100 text-slate-700">{ticket.id}</span>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded capitalize ${STATUS_STYLES[ticket.status]}`}>
                        {ticket.status.replace('_', ' ')}
                      </span>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded capitalize ${PRIORITY_STYLES[ticket.priority]}`}>
                        {ticket.priority}
                      </span>
                    </div>
                    <p className="text-sm font-semibold text-slate-800">{ticket.title}</p>
                    <p className="text-xs text-slate-500 mt-1">
                      {ticket.school} • Owner: {ticket.owner} • Created: {new Date(ticket.createdAt).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={() => updateStatus(ticket.id, 'in_progress')}>
                      <TimerReset className="h-4 w-4 mr-1" />
                      In Progress
                    </Button>
                    <Button size="sm" onClick={() => updateStatus(ticket.id, 'resolved')}>
                      <CircleCheckBig className="h-4 w-4 mr-1" />
                      Resolve
                    </Button>
                  </div>
                </div>
              </div>
            ))}
            {filteredTickets.length === 0 && (
              <p className="text-sm text-slate-500 py-6 text-center">No tickets match your filters.</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
